import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';

/** A qué se le pueden pegar etiquetas. */
export type TaggableType = 'project' | 'event' | 'station';

export interface Tag {
  id: number;
  name: string;
  color: string;
  entityType: TaggableType;
  companyId: number | null;
  createdByUserId?: number | null;
  /**
   * Cuántas entidades la llevan puesta.
   *
   * Lo trae el listado porque quien gestiona el vocabulario lo necesita para
   * decidir: retirar una etiqueta la despega de todo lo que la llevaba.
   */
  usageCount?: number;
}

/** Lo que devuelve la lectura en bloque: etiquetas por id de entidad. */
export type TagAssignments = Record<number, Array<Pick<Tag, 'id' | 'name' | 'color'>>>;

/**
 * Etiquetas personalizadas de proyectos, eventos y estaciones.
 *
 * **El vocabulario es de la marca.** Quien crea «Verano 2026» la deja
 * disponible para todo el equipo; si cada uno tuviera el suyo, dos personas
 * etiquetarían lo mismo de dos formas y ningún filtro cuadraría entre ellas.
 * Por eso `companyId` va en todas las llamadas: es la marca en la que se está
 * trabajando, no la compañía del token.
 *
 * **Los tres vocabularios están separados.** Las etiquetas de proyecto no se
 * ofrecen al etiquetar una estación aunque se llamen igual: describen cosas
 * distintas y mezclarlas llenaría la lista de ruido.
 */
export const tagsApi = {
  list: (companyId: number, entityType: TaggableType) =>
    api.get<Tag[]>('/xk-tag', { params: { companyId, entityType } }),

  create: (body: {
    companyId: number;
    entityType: TaggableType;
    name: string;
    color?: string;
  }) => api.post<Tag>('/xk-tag', body),

  update: (id: number, body: { companyId: number; name?: string; color?: string }) =>
    api.patch<Tag>(`/xk-tag/${id}`, body),

  remove: (id: number, companyId: number) =>
    api.delete<{ deleted: true }>(`/xk-tag/${id}`, { params: { companyId } }),

  /**
   * Qué lleva cada entidad, para una lista entera de una vez.
   *
   * POST y no GET porque los ids van en el cuerpo: una lista de 120 eventos no
   * cabe cómodamente en una URL.
   */
  assignments: (body: {
    companyId: number;
    entityType: TaggableType;
    entityIds: number[];
  }) => api.post<TagAssignments>('/xk-tag/assignments', body),

  /** Deja la entidad exactamente con estas etiquetas. Reemplaza. */
  setForEntity: (
    entityId: number,
    body: { companyId: number; entityType: TaggableType; tagIds: number[] },
  ) =>
    api.put<Array<Pick<Tag, 'id' | 'name' | 'color'>>>(
      `/xk-tag/assignments/${entityId}`,
      body,
    ),

  /** Qué entidades llevan TODAS estas etiquetas. */
  match: (body: {
    companyId: number;
    entityType: TaggableType;
    tagIds: number[];
  }) => api.post<{ entityIds: number[] }>('/xk-tag/match', body),
};

// ── Hooks ────────────────────────────────────────────────────────────────

/** El vocabulario disponible para etiquetar este tipo de entidad. */
export function useTags(companyId: number | null | undefined, entityType: TaggableType) {
  return useQuery({
    queryKey: queryKeys.tags.byType(companyId ?? 0, entityType),
    queryFn: () => tagsApi.list(companyId!, entityType),
    enabled: !!companyId,
    // Un vocabulario no cambia mientras alguien elige de él.
    staleTime: 60_000,
  });
}

/**
 * Las etiquetas de una lista de entidades.
 *
 * La clave incluye los ids porque una lista distinta es una respuesta distinta;
 * se ordenan para que el mismo conjunto en otro orden comparta caché en vez de
 * volver a pedirlo.
 */
export function useTagAssignments(
  companyId: number | null | undefined,
  entityType: TaggableType,
  entityIds: number[],
) {
  const ordenados = [...entityIds].sort((a, b) => a - b);
  return useQuery({
    queryKey: queryKeys.tags.assignments(companyId ?? 0, entityType, ordenados),
    queryFn: () =>
      tagsApi.assignments({ companyId: companyId!, entityType, entityIds: ordenados }),
    enabled: !!companyId && ordenados.length > 0,
  });
}

export function useCreateTag(companyId: number, entityType: TaggableType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; color?: string }) =>
      tagsApi.create({ companyId, entityType, ...body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tags.byType(companyId, entityType) });
    },
  });
}

export function useSetEntityTags(companyId: number, entityType: TaggableType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ entityId, tagIds }: { entityId: number; tagIds: number[] }) =>
      tagsApi.setForEntity(entityId, { companyId, entityType, tagIds }),
    onSuccess: () => {
      // Por prefijo: cada lista tiene su clave con sus ids, y tras marcar una
      // entidad todas las que la contengan están desfasadas.
      qc.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
}

export function useUpdateTag(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number; name?: string; color?: string }) =>
      tagsApi.update(id, { companyId, ...patch }),
    onSuccess: () => {
      // Por prefijo: renombrar cambia lo que se pinta en cada lista, no sólo en
      // el vocabulario.
      qc.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
}

export function useDeleteTag(companyId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tagsApi.remove(id, companyId),
    onSuccess: () => {
      // La etiqueta desaparece del vocabulario Y de todo lo que la llevaba, así
      // que las asignaciones ya cargadas también quedan desfasadas.
      qc.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
}

/**
 * Los ids que llevan todas las etiquetas marcadas.
 *
 * Devuelve `null` cuando no hay filtro puesto, y eso no es lo mismo que una
 * lista vacía: `null` significa «no filtres», `[]` significa «ninguna entidad
 * cumple». Confundirlos vacía la pantalla nada más entrar.
 */
export function useTagMatch(
  companyId: number | null | undefined,
  entityType: TaggableType,
  tagIds: number[],
) {
  const ordenados = [...tagIds].sort((a, b) => a - b);
  const q = useQuery({
    queryKey: queryKeys.tags.match(companyId ?? 0, entityType, ordenados),
    queryFn: () =>
      tagsApi.match({ companyId: companyId!, entityType, tagIds: ordenados }),
    enabled: !!companyId && ordenados.length > 0,
  });
  return {
    ...q,
    ids: ordenados.length === 0 ? null : (q.data?.entityIds ?? null),
  };
}
